/**
 * Utility functions for parsing stage markers from log lines
 *
 * Stage markers format:
 * - Stage start: "=== Stage: <name> ==="
 * - Stage completed: "=== Stage "<name>" completed (<duration>s) ==="
 * - Stage failed: "=== Stage "<name>" failed (<duration>s) ==="
 */

export interface LogLine {
  timestamp: string;
  stream: 'stdout' | 'stderr';
  line: string;
}

export type StageStatus = 'pending' | 'running' | 'success' | 'failed';

export interface Stage {
  name: string;
  status: StageStatus;
  duration?: string;
  logs: LogLine[];
  startIndex: number;
  endIndex?: number;
}

export interface StageGroup {
  /** Logs before any stage (setup logs) */
  preStage: LogLine[];
  /** Grouped stages */
  stages: Stage[];
}

// Regex patterns for stage markers
const STAGE_START_PATTERN = /^=== Stage: (.+) ===$/;
const STAGE_END_PATTERN = /^=== Stage "(.+)" (completed|failed) \(([0-9.]+)s\) ===$/;

/**
 * Check if a log line is a stage start marker
 */
export function isStageStart(line: string): { match: true; name: string } | { match: false } {
  const match = line.match(STAGE_START_PATTERN);
  if (match) {
    return { match: true, name: match[1] };
  }
  return { match: false };
}

/**
 * Check if a log line is a stage end marker
 */
export function isStageEnd(line: string): { match: true; name: string; status: 'success' | 'failed'; duration: string } | { match: false } {
  const match = line.match(STAGE_END_PATTERN);
  if (match) {
    return {
      match: true,
      name: match[1],
      status: match[2] === 'completed' ? 'success' : 'failed',
      duration: match[3],
    };
  }
  return { match: false };
}

/**
 * Parse logs into stage groups
 * Returns logs organized by stages with their statuses
 */
export function parseStages(logs: LogLine[]): StageGroup {
  const result: StageGroup = {
    preStage: [],
    stages: [],
  };

  let currentStage: Stage | null = null;

  for (let i = 0; i < logs.length; i++) {
    const log = logs[i];
    const trimmedLine = log.line.trim();

    // Check for stage start
    const startMatch = isStageStart(trimmedLine);
    if (startMatch.match) {
      // Close previous stage if any (shouldn't happen normally)
      if (currentStage) {
        currentStage.endIndex = i - 1;
        result.stages.push(currentStage);
      }

      // Start new stage
      currentStage = {
        name: startMatch.name,
        status: 'running',
        logs: [log],
        startIndex: i,
      };
      continue;
    }

    // Check for stage end
    const endMatch = isStageEnd(trimmedLine);
    if (endMatch.match && currentStage && currentStage.name === endMatch.name) {
      currentStage.logs.push(log);
      currentStage.status = endMatch.status;
      currentStage.duration = endMatch.duration;
      currentStage.endIndex = i;
      result.stages.push(currentStage);
      currentStage = null;
      continue;
    }

    // Regular log line
    if (currentStage) {
      currentStage.logs.push(log);
    } else {
      result.preStage.push(log);
    }
  }

  // Handle unclosed stage (still running)
  if (currentStage) {
    result.stages.push(currentStage);
  }

  return result;
}

/**
 * Check if logs contain any stage markers
 */
export function hasStages(logs: LogLine[]): boolean {
  return logs.some(log => isStageStart(log.line.trim()).match);
}
